import { NCWPartnerTabs, StakingOrigin, StakingType } from '@capsicum/common';
import { useHiddenNavigation, useRouter } from '@capsicum/common-web';
import { useCallback } from 'react';

/**
 * @description This hook contains all necessary logic needed to redirect to fan-token-staking.
 * If origin param is provided it will be used to redirect back to the entry point at the end of the staking flow.
 *
 * the originIds contains the needed param for the navigation to the origin IN THE CORRECT ORDER
 *
 * Example:
 * /fan-token-staking/{currencyId}/stake?origin={StakingOrigin.VOTES}&originIds={partnerId}&originIds={surveyId}
 *
 * const options = {origin: StakingOrigin.VOTES, originIds: [partnerId, surveyId]}
 **/
export const useFanTokenStakingRouter = () => {
	const { push, replace } = useRouter();
	const {
		goToHiddenNavigationEntryPoint,
		initHiddenNavigation,
		isHiddenNavigation,
		removeHiddenNavigation,
		saveHiddenNavigationPosition
	} = useHiddenNavigation();

	const getFormattedParams = (origin?: StakingOrigin, originIds?: Array<string>, amount?: string) => {
		const params = new URLSearchParams({
			...(amount && { amount }),
			...(origin && { origin })
		});

		originIds?.forEach(id => params.append('originIds', id));

		return params;
	};

	const handleNavigationToOrigin = ({ origin, originIds }: { origin: StakingOrigin; originIds: Array<string> }) => {
		if (isHiddenNavigation) {
			goToHiddenNavigationEntryPoint();

			return;
		}

		const routes: Partial<Record<StakingOrigin, string>> = {
			[StakingOrigin.PARTNER_PAGE]: originIds?.[0] ? `/partner/${originIds[0]}` : '/wallet',
			[StakingOrigin.TYPEFORM]: originIds?.[0]
				? `/partner/${originIds[0]}?tab=${NCWPartnerTabs.NCFanZone}`
				: '/wallet',
			[StakingOrigin.VOTES]: originIds?.length > 1 ? `/votes/${originIds[0]}/${originIds[1]}` : '/wallet'
		};

		replace(routes[origin] ?? '/wallet');
	};

	const handleNavigationToStake = useCallback(
		({
			currencyId,
			currentStake,
			origin,
			originIds,
			withHiddenNavigation = false
		}: {
			currencyId?: string;
			currentStake?: string;
			origin?: StakingOrigin;
			originIds?: Array<string>;
			withHiddenNavigation?: boolean;
		}) => {
			if (!currencyId) return;

			if (Number(currentStake) > 0) {
				navigateToFanTokenStakingOverview({ currencyId, origin, originIds, withHiddenNavigation });
			} else {
				navigateToFanTokenStaking({ currencyId, origin, originIds, withHiddenNavigation });
			}
		},
		[]
	);

	const navigateToFanTokenStakingOverview = useCallback(
		({
			currencyId,
			origin,
			originIds,
			withHiddenNavigation = false
		}: {
			currencyId?: string;
			currentStake?: string;
			origin?: StakingOrigin;
			originIds?: Array<string>;
			withHiddenNavigation?: boolean;
		}) => {
			if (!currencyId) return;
			const params = getFormattedParams(origin, originIds);
			const stakingOverviewRoute = `/fan-token-staking/${currencyId}/stake-overview?${params.toString()}`;

			if (withHiddenNavigation) {
				initHiddenNavigation(stakingOverviewRoute);
			} else {
				// remove hidden navigation if it exists just in case to avoid wrong re directions
				isHiddenNavigation && removeHiddenNavigation();

				push(stakingOverviewRoute);
			}
		},
		[push]
	);

	const navigateToStakeAllFanTokens = useCallback(
		({ origin, originIds }: { origin?: StakingOrigin; originIds?: Array<string> }) => {
			const params = getFormattedParams(origin, originIds);

			push(`/fan-token-staking/stake-all?${params.toString()}`);
		},
		[push]
	);

	const navigateToFanTokenStaking = useCallback(
		({
			currencyId,
			origin,
			originIds,
			withHiddenNavigation
		}: {
			currencyId?: string;
			currentStake?: string;
			origin?: StakingOrigin;
			originIds?: Array<string>;
			withHiddenNavigation?: boolean;
		}) => {
			if (!currencyId) return;
			const params = getFormattedParams(origin, originIds);
			const stakingRoute = `/fan-token-staking/${currencyId}/${StakingType.STAKE}?${params.toString()}`;

			if (withHiddenNavigation) {
				initHiddenNavigation(stakingRoute);
			} else {
				// remove hidden navigation if it exists just in case to avoid wrong re directions
				isHiddenNavigation && removeHiddenNavigation();
				push(stakingRoute);
			}
		},
		[push]
	);

	const navigateToFanTokenUnstaking = useCallback(
		({
			currencyId,
			origin,
			originIds
		}: {
			currencyId?: string;
			origin?: StakingOrigin;
			originIds?: Array<string>;
		}) => {
			if (!currencyId) return;
			const params = getFormattedParams(origin, originIds);

			push(`/fan-token-staking/${currencyId}/${StakingType.UNSTAKE}?${params.toString()}`);
		},
		[push]
	);

	const navigateToFanTokenStakingReviewPage = useCallback(
		({
			amount,
			currencyId,
			origin,
			originIds,
			stakingType
		}: {
			amount?: string;
			currencyId?: string;
			origin?: StakingOrigin;
			originIds?: Array<string>;
			stakingType: StakingType;
		}) => {
			if (!currencyId || !amount) return;

			const params = getFormattedParams(origin, originIds, amount);
			const reviewRoute = `/fan-token-staking/${currencyId}/${stakingType}/review?${params.toString()}`;

			if (isHiddenNavigation) {
				saveHiddenNavigationPosition(reviewRoute);
			} else {
				push(reviewRoute);
			}
		},
		[push]
	);

	return {
		handleNavigationToOrigin,
		handleNavigationToStake,
		navigateToFanTokenStaking,
		navigateToFanTokenStakingReviewPage,
		navigateToFanTokenUnstaking,
		navigateToStakeAllFanTokens
	};
};
